import type {
	CampaignQuestion,
	CampaignQuestionDraft,
	CampaignQuestionType,
} from '@/types/campaign'

const CONDITION_SOURCE_TYPES: CampaignQuestionType[] = [
	'BOOLEAN',
	'SINGLE_CHOICE',
	'MULTIPLE_CHOICE',
]

const isChoice = (type: CampaignQuestionType) =>
	type === 'SINGLE_CHOICE' || type === 'MULTIPLE_CHOICE'

export function createQuestionDraft(
	type: CampaignQuestionType = 'SHORT_TEXT',
): CampaignQuestionDraft {
	return {
		key: crypto.randomUUID(),
		type,
		label: '',
		helpText: '',
		required: false,
		options: isChoice(type) ? ['', ''] : [],
		scaleMin: 1,
		scaleMax: 5,
		condition: null,
	}
}

export function draftFromQuestion(
	question: CampaignQuestion,
): CampaignQuestionDraft {
	return {
		key: question.id,
		type: question.type,
		label: question.label,
		helpText: question.helpText ?? '',
		required: question.required,
		options: question.options,
		scaleMin: question.scaleMin ?? 1,
		scaleMax: question.scaleMax ?? 5,
		condition: question.condition
			? {
					questionKey: question.condition.questionId,
					operator: question.condition.operator,
					value: question.condition.value,
				}
			: null,
	}
}

/** Valores que a pergunta de origem aceita numa condição */
export function conditionValues(source: CampaignQuestionDraft): string[] {
	return source.type === 'BOOLEAN'
		? ['true', 'false']
		: source.options.filter(Boolean)
}

/** Perguntas anteriores a `index` que podem servir de origem para uma condição */
export function conditionSources(
	drafts: CampaignQuestionDraft[],
	index: number,
): CampaignQuestionDraft[] {
	return drafts
		.slice(0, index)
		.filter((draft) => CONDITION_SOURCE_TYPES.includes(draft.type))
}

/**
 * Remove as condições que deixaram de fazer sentido (pergunta de origem apagada, movida para depois,
 * trocada de tipo ou com a opção removida), para o formulário nunca enviar uma condição inválida.
 */
export function sanitizeConditions(
	drafts: CampaignQuestionDraft[],
): CampaignQuestionDraft[] {
	return drafts.map((draft, index) => {
		if (!draft.condition) return draft
		const { questionKey, value } = draft.condition
		const source = conditionSources(drafts, index).find(
			(candidate) => candidate.key === questionKey,
		)
		return source && conditionValues(source).includes(value)
			? draft
			: { ...draft, condition: null }
	})
}

/** Chave i18n do primeiro problema encontrado, ou null quando o formulário está pronto para salvar */
export function validateDrafts(drafts: CampaignQuestionDraft[]): string | null {
	if (drafts.length === 0) return 'campaigns.form.errors.noQuestions'
	for (const draft of drafts) {
		if (draft.label.trim().length < 3)
			return 'campaigns.form.errors.questionLabel'
		if (isChoice(draft.type)) {
			const options = draft.options.map((option) => option.trim())
			if (
				options.length < 2 ||
				options.some((option) => !option) ||
				new Set(options).size !== options.length
			) {
				return 'campaigns.form.errors.options'
			}
		}
		if (draft.type === 'SCALE' && draft.scaleMin >= draft.scaleMax) {
			return 'campaigns.form.errors.scale'
		}
	}
	return null
}

/** Normaliza o que vai para a API: textos aparados e opções só nas perguntas de escolha */
export function toQuestionPayload(
	drafts: CampaignQuestionDraft[],
): CampaignQuestionDraft[] {
	return drafts.map((draft) => ({
		...draft,
		label: draft.label.trim(),
		helpText: draft.helpText.trim(),
		options: isChoice(draft.type)
			? draft.options.map((option) => option.trim())
			: [],
	}))
}
